import React, { Suspense } from 'react'
import type {
  TableData,
  ExecutionResults,
  PlotlyObject,
} from '@stratascratch/code-editor'
import { Alert } from '@stratascratch/ui'
import Results from './Results'
import TestOutput from './TestOutput'

const ExpectedOutput = React.lazy(() =>
  import('@stratascratch/code-editor').then((mod) => ({
    default: mod.ExpectedOutput,
  })),
)

interface Props {
  value:
    | {
        output: TableData | { input: string; output: string }[]
        executionResults: ExecutionResults | null
        questionClass: 'Coding Question'
      }
    | {
        output: TableData | { input: string; output: string }[]
        executionResults: ExecutionResults | null
        questionClass: 'Algorithm Question'
      }
    | {
        output: string | null
        executionResults: ExecutionResults | null
        questionClass: 'Visual Question'
        visualCodeType: 'matplotlib'
      }
    | {
        plotly: PlotlyObject
        executionResults: ExecutionResults | null
        questionClass: 'Visual Question'
        visualCodeType: 'plotly'
      }
}

function ExpectedOutputWidget({ value }: Props) {
  const { executionResults, questionClass = 'Coding Question' } = value
  const isSSR = typeof window === 'undefined'

  // Handle Algorithm Questions
  if (
    value.questionClass === 'Algorithm Question' &&
    Array.isArray(value.output)
  ) {
    return (
      <div>
        <h5 className="mt-6 text-base font-bold">Expected Output</h5>
        {value.output.map((test, index) => (
          <div key={index}>
            <TestOutput>
              <div className="px-4 py-2">
                <span className="text-base font-bold">Test {index + 1}</span>
                <div className="mt-4">
                  <h5 className="font-bold">Official output:</h5>
                  <span className="mt-4">{test.output}</span>
                </div>
              </div>
            </TestOutput>
          </div>
        ))}
        {executionResults && (
          <Results
            results={executionResults}
            isLoading={false}
            questionClass={questionClass}
          />
        )}
      </div>
    )
  }

  // Handle Visual Questions
  if (value.questionClass === 'Visual Question') {
    const { visualCodeType } = value

    return (
      <div>
        <h5 className="mt-6 px-4 text-base font-bold">
          Expected Visual Output
        </h5>
        {visualCodeType === 'plotly' && !isSSR && (
          <Suspense fallback={<div>Loading Plotly</div>}>
            <div className="m-2">
              <ExpectedOutput results={value} loading={false} isGraph />
            </div>
          </Suspense>
        )}
        {visualCodeType === 'matplotlib' && (
          <div className="m-2">
            <img
              src={`data:image/png;base64,${value.output}`}
              alt="Expected Output"
            />
          </div>
        )}
        {executionResults && (
          <Results
            questionClass={questionClass}
            results={executionResults}
            isLoading={false}
          />
        )}
      </div>
    )
  }

  const { output } = value

  if (!output) {
    return (
      <Alert type="danger" noIcon className="QuestionResults__error-message">
        An error loading the results has occurred
      </Alert>
    )
  }

  // Handle Coding Questions (default case)
  return (
    <div>
      {!isSSR && (
        <Suspense fallback={<div>Loading Output</div>}>
          <ExpectedOutput loading={!output} results={output as TableData} />
        </Suspense>
      )}
      {executionResults && (
        <Results
          results={executionResults}
          isLoading={false}
          questionClass={questionClass}
        />
      )}
    </div>
  )
}

export default ExpectedOutputWidget
